import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import MenuItem from '@material-ui/core/MenuItem';
import Divider from '@material-ui/core/Divider';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import MenuIcon from '@material-ui/icons/Menu';
import { injectIntl } from 'react-intl';

const drawerWidth = 240;
const drawerCollapsedWidth = 56;
const largeWidth = 1024; // >=largeWidth: show drawer by default;
const smallWidth = 512;

const hideButtonHeight = '45px';

const ScreenSizeContext = React.createContext({ screenSize: 'large', collapsed: 'false' });

const styles = theme => ({
  drawer: {
    position: 'relative',
    zIndex: 1000,
  },
  drawerPaper: {
    maxHeight: '100%',
    borderTopLeftRadius: '0px',
    borderBottomLeftRadius: '0px',
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
  },
  drawerPaperHidden: {
    position: 'fixed',
    height: `calc(${hideButtonHeight})`,
    transition: theme.transitions.create('height', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.short,
    }),
  },
  drawerPaperHiddenPadded: {
    position: 'fixed',
    height: `calc(${theme.mixins.toolbar} + ${hideButtonHeight})`,
    transition: theme.transitions.create('height', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.short,
    }),
  },
  drawerPaperSmallScreen: {
  },
  drawerPaperExpanded: {
    width: drawerWidth,
    transition: theme.transitions.create(['width', 'height'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.short,
    }),
  },
  drawerPaperCollapsed: {
    transition: theme.transitions.create(['width', 'height'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.short,
    }),
    width: drawerCollapsedWidth + 1,
    [theme.breakpoints.up('sm')]: {
      width: drawerCollapsedWidth + 1,
    },
  },
  toolbar: theme.mixins.toolbar,
  toggleButton: {
    position: 'absolute',
    bottom: '0px',
    width: drawerWidth,
  },
});

const defaultCollapsed = { mediumCollapsed: true, largeCollapsed: false };

class SideDrawer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ...props.collapseLeftDrawer ? { mediumCollapsed: true, largeCollapsed: true} : defaultCollapsed,
      hidden: true,
      windowWidth: 0,
    };
  }

  componentWillMount() {
    this.updateWindowWidth();
  }

  componentDidMount() {
    const { collapseLeftDrawer } = this.props;
    window.addEventListener('resize', this.updateWindowWidth);
    const savedCollapsedState = {};
    ['mediumCollapsed', 'largeCollapsed'].forEach((key) => {
      try {
        const state = JSON.parse(window.localStorage.getItem(key));
        if (collapseLeftDrawer) savedCollapsedState[key] = true; // Collapse drawer via props
        else if (typeof state === 'boolean') savedCollapsedState[key] = state;
      } catch (err) { }
    });
    this.setState(savedCollapsedState);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { collapseLeftDrawer } = this.props;
    if (collapseLeftDrawer !== prevProps.collapseLeftDrawer) {
      this.setState({
        ...collapseLeftDrawer ? { mediumCollapsed: true, largeCollapsed: true} : defaultCollapsed,
      })
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowWidth);
  }

  updateWindowWidth = () => {
    const prevScreenSize = this.screenSize();
    this.setState({ windowWidth: window.innerWidth });
    // on screen size change, reset state to default
    if (this.screenSize() !== prevScreenSize) {
      // reset to defaults
      this.setState({ hidden: true });
    }
  };

  screenSize = () => {
    const { windowWidth } = this.state;
    if (windowWidth > largeWidth) return 'large';
    if (windowWidth > smallWidth) return 'medium';
    return 'small';
  };

  collapsed = () => {
    const { mediumCollapsed, largeCollapsed } = this.state;
    if (this.hidden()) return true; // when hidden, width should be 58px

    const screenSize = this.screenSize();
    if (screenSize === 'medium') {
      return mediumCollapsed;
    }
    if (screenSize === 'large') {
      return largeCollapsed;
    }
    return false;
  };

  hidden = () => {
    return this.screenSize() === 'small' && this.state.hidden;
  };

  handleToggle = (event) => {
    const screenSize = this.screenSize();
    let { mediumCollapsed, largeCollapsed } = this.state;

    if (screenSize === 'medium') {
      mediumCollapsed = !mediumCollapsed;
    }
    if (screenSize === 'large') {
      largeCollapsed = !largeCollapsed;
    }
    this.setState({ mediumCollapsed, largeCollapsed }, () => {
      window.localStorage.setItem('mediumCollapsed', JSON.stringify(mediumCollapsed));
      window.localStorage.setItem('largeCollapsed', JSON.stringify(largeCollapsed));
    });
  };

  handleHide = (event) => {
    this.setState({ hidden: !this.state.hidden });
  };

  render() {
    const {
      classes, children, padding, anchor, intl
    } = this.props;
    const padded = padding ? <div className={classes.toolbar} /> : null;
    const toggleButton = anchor === "left" && (
      !this.hidden()
      && (
        <div className={classes.toggleButton}>
          <Divider />
          <MenuItem
            button
            onClick={this.handleToggle}
          >
            {this.collapsed()
              ? <ChevronRightIcon />
              : <ChevronLeftIcon />}
            {!this.collapsed() && intl.formatMessage({ id: 'collapse' })}
          </MenuItem>
        </div>
      )
    )
    const hideButton = (
      this.screenSize() === 'small'
      && (
        <MenuItem
          button
          onClick={this.handleHide}
        >
          <MenuIcon />
        </MenuItem>
      )
    );
    const drawerMainMenu = (
      !this.hidden() && (
        <div>
          <ScreenSizeContext.Provider
            value={{ screenSize: this.screenSize(), collapsed: this.collapsed() }}
          >
            {children}
          </ScreenSizeContext.Provider>
          {toggleButton}
        </div>
      )
    );
    const conditionalStylingClasses = {
      [classes.drawerPaperExpanded]: !this.collapsed(),
      [classes.drawerPaperCollapsed]: this.collapsed(),
      [classes.drawerPaperHidden]: this.hidden() && !padding,
      [classes.drawerPaperHiddenPadded]: this.hidden() && padding,
      [classes.drawerPaperSmallScreen]: this.screenSize() === 'small',
    };
    return (
      <Drawer
        variant="permanent"
        anchor={anchor}
        open={!this.collapsed()}
        className={classNames(classes.drawerPaper, classes.drawer, conditionalStylingClasses)}
        classes={{
          paper: classNames(classes.drawerPaper, conditionalStylingClasses),
        }}
      >
        {padded}
        {hideButton}
        {drawerMainMenu}
      </Drawer>
    );
  }
}

SideDrawer.defaultProps = {
  children: null,
  padding: true,
  anchor: 'left',
};

SideDrawer.propTypes = {
  children: PropTypes.instanceOf(Object),
  classes: PropTypes.instanceOf(Object).isRequired,
  padding: PropTypes.bool,
  anchor: PropTypes.string,
};

export default withStyles(styles, { withTheme: true })(injectIntl(SideDrawer));
export { ScreenSizeContext };
